<script lang="ts">
  /**
   * One parameter: slider / toggle / select generated from its ParamDef, plus oscillate (∿: swing
   * around the set value), modulation (~: drive it from the music) and MIDI learn.
   */
  import type { AppController } from '../../app/controller.svelte';
  import { OSC_SPEEDS } from '../../core/variety';
  import { LFO_SHAPES, type LfoShape } from '../../shared/modulation';
  import type { ParamDef } from '../../shared/params';
  import ModEditor from './ModEditor.svelte';
  import Icon from './Icon.svelte';

  interface Props {
    ctl: AppController;
    def: ParamDef;
    value: number;
    /** Modulation target path (layer/<id>/<param>, fx/<id>/<param>, palette/shift…). */
    target: string;
    /** MIDI path (layer#<index>/<param> so mappings survive preset changes). */
    midiPath?: string;
    /** False for values the modulation matrix can't drive (some settings). */
    modulatable?: boolean;
    onchange: (v: number) => void;
  }
  let { ctl, def, value, target, midiPath, modulatable = true, onchange }: Props = $props();
  let open = $state(false);
  let oscOpen = $state(false);
  const osc = $derived(ctl.oscillationOf(target));
  const mods = $derived(
    ctl.scene.modulations.filter((m) => m.target === target && m.source !== 'osc'),
  );
  const oscSpec = $derived({
    beats: osc?.osc?.rate ?? 8,
    depth: osc?.amount ?? 0.25,
    shape: (osc?.osc?.shape ?? 'sine') as LfoShape,
  });
  function toggleOsc() {
    if (!osc) {
      ctl.setOscillation(target, oscSpec);
      oscOpen = true;
    } else oscOpen = !oscOpen;
  }
  const kind = $derived(def.control ?? 'slider');
  const decimals = $derived((def.step ?? 0.01) >= 1 ? 0 : def.max - def.min > 10 ? 1 : 2);
  const learning = $derived(
    ctl.midi.learning?.kind === 'param' &&
      ctl.midi.learning.path === (midiPath ?? target) &&
      ctl.midiVersion >= 0,
  );
  const bound = $derived(
    ctl.midiVersion >= 0 &&
      ctl.midi.bindings.some(
        (b) => b.target.kind === 'param' && b.target.path === (midiPath ?? target),
      ),
  );
</script>

<div class="param" data-testid={`param-${def.id}`}>
  <span class="label" title={def.description ?? def.label}>{def.label}</span>
  {#if kind === 'toggle'}
    <label class="toggle-wrap">
      <input
        type="checkbox"
        aria-label={def.label}
        checked={value >= 0.5}
        onchange={(e) => onchange(e.currentTarget.checked ? 1 : 0)}
      />
      <span class="muted small">{value >= 0.5 ? 'On' : 'Off'}</span>
    </label>
  {:else if kind === 'select' && def.options}
    <select
      aria-label={def.label}
      value={String(Math.round(value))}
      onchange={(e) => onchange(Number(e.currentTarget.value))}
    >
      {#each def.options as o, i (o)}
        <option value={String(i)}>{o}</option>
      {/each}
    </select>
  {:else}
    <input
      type="range"
      aria-label={def.label}
      min={def.min}
      max={def.max}
      step={def.step ?? (def.max - def.min) / 500}
      {value}
      oninput={(e) => onchange(Number(e.currentTarget.value))}
    />
  {/if}
  <span class="value">{kind === 'slider' ? value.toFixed(decimals) : ''}</span>
  <span class="tools">
    {#if modulatable && kind === 'slider'}
      <button
        class="icon-btn"
        class:on={!!osc}
        title={osc ? 'Oscillating (click for speed/depth)' : 'Oscillate around this value'}
        aria-label={`Oscillate ${def.label}`}
        aria-expanded={oscOpen}
        data-testid={`osc-${def.id}`}
        onclick={toggleOsc}><Icon name="wave" /></button
      >
    {/if}
    {#if modulatable}
      <button
        class="icon-btn"
        class:on={mods.length > 0 || open}
        title={mods.length
          ? `${mods.length} modulation${mods.length > 1 ? 's' : ''}`
          : 'Modulate this parameter'}
        aria-label={`Modulate ${def.label}`}
        aria-expanded={open}
        data-testid={`mod-${def.id}`}
        onclick={() => (open = !open)}
        ><Icon name="modulate" />{#if mods.length}{mods.length}{/if}</button
      >
    {/if}
    {#if ctl.midiVersion >= 0 && ctl.midi.enabled}
      <button
        class="icon-btn"
        class:on={learning || bound}
        title={learning
          ? 'Move a knob or fader…'
          : bound
            ? 'MIDI mapped (click to re-learn)'
            : 'MIDI learn'}
        aria-label={`MIDI learn ${def.label}`}
        onclick={() =>
          ctl.midiLearn(
            learning ? null : { kind: 'param', path: midiPath ?? target, label: def.label },
          )}>{learning ? '…' : 'M'}</button
      >
    {/if}
  </span>
</div>
{#if oscOpen && osc}
  <div class="osc" data-testid="osc-editor">
    <select
      aria-label="Oscillation speed"
      value={String(oscSpec.beats)}
      onchange={(e) =>
        ctl.setOscillation(target, { ...oscSpec, beats: Number(e.currentTarget.value) })}
    >
      {#each OSC_SPEEDS as b (b)}<option value={String(b)}>{b} beat{b > 1 ? 's' : ''}</option
        >{/each}
    </select>
    <select
      aria-label="Oscillation shape"
      value={oscSpec.shape}
      onchange={(e) =>
        ctl.setOscillation(target, { ...oscSpec, shape: e.currentTarget.value as LfoShape })}
    >
      {#each LFO_SHAPES as s (s)}<option value={s}>{s}</option>{/each}
    </select>
    <input
      type="range"
      min="0.02"
      max="1"
      step="0.01"
      aria-label="Oscillation depth"
      title="Depth"
      value={oscSpec.depth}
      oninput={(e) =>
        ctl.setOscillation(target, { ...oscSpec, depth: Number(e.currentTarget.value) })}
    />
    <button
      class="link"
      onclick={() => {
        ctl.setOscillation(target, null);
        oscOpen = false;
      }}>Stop</button
    >
  </div>
{/if}
{#if open}
  <ModEditor {ctl} {target} label={def.label} />
{/if}

<style>
  .param {
    display: grid;
    grid-template-columns: 92px 1fr 40px auto;
    align-items: center;
    gap: 6px;
    min-height: 28px;
  }
  .label {
    color: var(--muted);
    font-size: 12.5px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .value {
    font-family: var(--mono);
    font-size: 11px;
    color: var(--muted);
    text-align: right;
  }
  input[type='range'] {
    width: 100%;
    accent-color: var(--accent);
  }
  select {
    width: 100%;
  }
  .toggle-wrap {
    display: flex;
    gap: 8px;
    align-items: center;
  }
  .toggle-wrap input {
    accent-color: var(--accent);
  }
  .osc {
    display: grid;
    grid-template-columns: auto auto 1fr auto;
    gap: 6px;
    align-items: center;
    margin: 2px 0 6px 98px;
    font-size: 12px;
  }
  .osc select {
    font-size: 11.5px;
    padding: 2px 4px;
  }
  .osc input {
    accent-color: var(--accent-2);
  }
  .tools {
    display: flex;
    gap: 2px;
    min-width: 30px;
    justify-content: flex-end;
  }
</style>
